import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { Dispatch, HTMLProps, RefObject } from 'react';

import type { CheckboxState } from '@adsk/alloy-react-checkbox';
import {
  BackwardsCompatibleMutableRefObject,
  mergeRefs,
  noop,
} from '@adsk/alloy-react-helpers';
import { useOverlay } from '@adsk/alloy-react-overlay';
import type { StylableComponent, StyleProp } from '@adsk/alloy-react-theme';

import useScrollIntoView from './useScrollIntoView';
import useTree, { UseTreeProps } from './useTree';
import { TREE_ACTIONS } from '../consts';
import { defaultGetLabelFromNode } from '../helpers/defaultGetters';
import type {
  BaseAction,
  DefaultInputTreeNode,
  DropdownTreeGetMenuProps,
  IdType,
  InternalDataNode,
  TreeSelectedIds,
  TreeState,
} from '../types';

const NAVIGATION_KEYS = [
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'Space',
  'Enter',
  'Home',
  'End',
];

export type UseDropdownTree<InputTreeNode extends DefaultInputTreeNode> =
  UseTreeProps<InputTreeNode> & {
    filterBy?: (item: string, inputValue: string) => boolean;
    initialIsOpen?: boolean;
    initialFilterValue?: string;
    isAutocomplete?: boolean;
    enableSearchHighlight?: boolean;
  };

const reFocus = ({
  isAutocomplete,
  inputRef,
  toggleButtonRef,
}: {
  isAutocomplete: boolean;
  inputRef: RefObject<HTMLElement | null>;
  toggleButtonRef: RefObject<HTMLElement | null>;
}) => {
  if (isAutocomplete) {
    inputRef.current?.focus();
  } else {
    toggleButtonRef.current?.focus();
  }
};

const _onBlur = ({
  e,
  isAutocomplete,
  closeMenu,
  treeOnBlur,
  setFilterValue,
}: {
  e: React.FocusEvent;
  isAutocomplete: boolean;
  closeMenu: () => void;
  treeOnBlur: (e: React.FocusEvent) => void;
  setFilterValue: (val: string) => void;
}) => {
  closeMenu();
  // Clear filter value when closing the dropdown to match autocomplete behavior
  if (isAutocomplete) {
    setFilterValue('');
    treeOnBlur?.(e);
  }
};

const preventOnBlurWithinComponent = ({
  e,
  isAutocomplete,
  inputRef,
  toggleButtonRef,
  controlRef,
  menuRef,
  closeMenu,
  treeOnBlur,
  setFilterValue,
}: {
  e: React.FocusEvent;
  isAutocomplete: boolean;
  inputRef: RefObject<HTMLElement | null>;
  toggleButtonRef: RefObject<HTMLElement | null>;
  controlRef: RefObject<HTMLElement | null>;
  menuRef: RefObject<HTMLElement | null>;
  closeMenu: () => void;
  treeOnBlur: (e: React.FocusEvent) => void;
  setFilterValue: (val: string) => void;
}) => {
  if (toggleButtonRef.current?.contains(e.relatedTarget)) {
    reFocus({ isAutocomplete, inputRef, toggleButtonRef });
    return;
  }

  if (controlRef.current?.contains(e.relatedTarget)) {
    reFocus({ isAutocomplete, inputRef, toggleButtonRef });
    return;
  }

  if (menuRef.current?.contains(e.relatedTarget)) {
    reFocus({ isAutocomplete, inputRef, toggleButtonRef });
    return;
  }

  _onBlur({ e, isAutocomplete, closeMenu, treeOnBlur, setFilterValue });
};

const filterNodes = <InputTreeNode extends DefaultInputTreeNode>({
  isAutocomplete,
  filterValue,
  prevFilterValueRef,
  flatTreeData,
  getLabelFromNode,
  filterBy,
  dispatch,
}: {
  isAutocomplete: boolean;
  filterValue: string;
  prevFilterValueRef: BackwardsCompatibleMutableRefObject<string | null>;
  flatTreeData: InternalDataNode<InputTreeNode>[];
  getLabelFromNode: (node: InputTreeNode) => string;
  filterBy: (item: string, inputValue: string) => boolean;
  dispatch: Dispatch<BaseAction<InputTreeNode>>;
}) => {
  if (!isAutocomplete) {
    return;
  }

  if (filterValue === prevFilterValueRef.current) {
    // Don't filter if the input didn't change
    return;
  }

  if (!filterValue && !prevFilterValueRef.current) {
    // Don't filter if we move between undefined/null/empty string
    return;
  }

  prevFilterValueRef.current = filterValue;
  const matchingNodeIds = flatTreeData
    .filter((node) => filterBy(getLabelFromNode(node.original), filterValue))
    .map((node) => node.id);
  dispatch({
    type: TREE_ACTIONS.filterNodes,
    payload: { ids: matchingNodeIds },
  });
};

const generateSelectedItems = <InputTreeNode extends DefaultInputTreeNode>({
  selectedIds,
  normalizedTree,
}: {
  selectedIds: TreeSelectedIds | undefined;
  normalizedTree: Record<IdType, InternalDataNode<InputTreeNode>>;
}): [
  InternalDataNode<InputTreeNode>[] | undefined,
  InternalDataNode<InputTreeNode> | undefined,
] => {
  if (selectedIds === undefined) {
    return [undefined, undefined];
  }

  const selected = Object.entries(selectedIds)
    .filter(([id, isSelected]) => isSelected === true && normalizedTree[id])
    .map(([id]) => normalizedTree[id]);

  return [selected, selected[0] || null];
};

const _handleKeyDown = ({
  e,
  isOpen,
  isAutocomplete,
  highlightedId,
  selectedIds,
  openMenu,
  closeMenu,
  setFilterValue,
  isNodeMultiSelectable,
  onSelect,
  treeOnKeyDown,
}: {
  e: React.KeyboardEvent<HTMLElement>;
  isOpen: boolean;
  isAutocomplete: boolean;
  highlightedId: IdType | undefined;
  selectedIds: TreeSelectedIds | undefined;
  openMenu: () => void;
  closeMenu: () => void;
  setFilterValue: (val: string) => void;
  isNodeMultiSelectable: (id: IdType) => boolean;
  onSelect:
    | ((p: { isSelected: CheckboxState; id: IdType }) => void)
    | undefined;
  treeOnKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void;
}) => {
  e.stopPropagation();

  switch (e.key) {
    case 'ArrowDown':
      openMenu();
      break;
    case 'Escape':
      if (!isOpen) {
        break;
      }

      setFilterValue('');
      closeMenu();
      break;
    case 'Enter':
      if (!isOpen) {
        openMenu();
        break;
      }

      if (
        isAutocomplete &&
        highlightedId !== undefined &&
        isNodeMultiSelectable(highlightedId)
      ) {
        // Switch enter from toggling expanded state to selection
        // Because space is already used for search filtering
        // Then toggle selection of the highlighted node

        const currentSelectionState = selectedIds && selectedIds[highlightedId];

        const updatedSelectionState = currentSelectionState !== true;

        onSelect?.({
          isSelected: updatedSelectionState,
          id: highlightedId,
        });

        // return so we don't call into tree's onKeyDown or it'll expand/collapse
        return;
      }
      break;
    default:
      break;
  }

  if (NAVIGATION_KEYS.includes(e.key) && isOpen) {
    treeOnKeyDown(e);
  }
};

const getSelectedItemProps = () => ({});

const _mergeRefs = ({
  isAutocomplete,
  isOpen,
  focusRef,
  inputRef,
  toggleButtonRef,
}: {
  isAutocomplete: boolean;
  isOpen: boolean;
  focusRef: RefObject<HTMLElement | null>;
  inputRef: RefObject<HTMLElement | null>;
  toggleButtonRef: RefObject<HTMLElement | null>;
}) => {
  if (isAutocomplete) {
    mergeRefs(focusRef, inputRef);
  } else {
    mergeRefs(focusRef, toggleButtonRef);
  }

  if (isOpen && focusRef.current) {
    focusRef.current.focus();
  }
};

const useDropdownTree = <InputTreeNode extends DefaultInputTreeNode>({
  selectedIds,
  filterBy = (item, val) => item.includes(val),
  initialIsOpen = false,
  initialFilterValue = '',
  onSelect: rawOnSelect,
  getLabelFromNode = defaultGetLabelFromNode,
  isAutocomplete = false,
  enableSearchHighlight,
  reducer: reducerProp = (state) => state,
  ...props
}: UseDropdownTree<InputTreeNode>) => {
  const nodes = useMemo(() => new Map<IdType, Element | null>(), []);
  const [filterValue, setFilterValue] = useState(initialFilterValue);
  const prevFilterValueRef = useRef<string | null>(null); // only filter if value changes

  const inputRef = useRef<HTMLInputElement | null>(null);
  const toggleButtonRef = useRef<HTMLButtonElement | null>(null);
  const controlRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLUListElement | null>(null);

  const [isOpen, setIsOpen] = useState(initialIsOpen);

  const { overlayProps } = useOverlay({
    placement: 'bottom-start',
    show: isOpen,
    target: controlRef,
    overlayRef: menuRef,
  });

  const openMenu = useCallback(() => setIsOpen(true), []);
  const closeMenu = useCallback(() => setIsOpen(false), []);
  const toggleMenuOpen = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const getNodeById = useCallback((id: IdType) => nodes.get(id), [nodes]);

  const { scrollIntoViewReducer } = useScrollIntoView<InputTreeNode>({
    getNodeById,
  });

  const useDropdownTreeReducer = useCallback(
    (
      prevState: TreeState<InputTreeNode>,
      action: BaseAction<InputTreeNode>,
    ) => {
      const updatedState = scrollIntoViewReducer(prevState, action);

      return reducerProp(updatedState, action);
    },
    [reducerProp, scrollIntoViewReducer],
  );

  const isNodeMultiSelectable = useCallback(
    (id: IdType) => !!document.getElementById(`${id}-checkbox`),
    [],
  );

  const onSelect = useCallback(
    (p: { isSelected: CheckboxState; id: IdType }) => {
      rawOnSelect?.(p);

      if (!isNodeMultiSelectable(p.id)) {
        closeMenu();
        // Clear filter value for single-select when item is selected
        if (isAutocomplete) {
          setFilterValue('');
        }
      }
    },
    [
      isNodeMultiSelectable,
      rawOnSelect,
      closeMenu,
      isAutocomplete,
      setFilterValue,
    ],
  );

  const {
    normalizedTree,
    orderedIds,
    highlightedId,
    getTreeNodeProps: rawGetTreeNodeProps,
    getTreeProps,
    dispatch,
    ...useTreeReturnValue
  } = useTree({
    reducer: useDropdownTreeReducer,
    selectedIds,
    onSelect,
    getLabelFromNode,
    ...props,
  });

  const flatTreeData = useMemo(
    () => orderedIds.map((id) => normalizedTree[id]),
    [orderedIds, normalizedTree],
  );

  useEffect(() => {
    filterNodes({
      isAutocomplete,
      filterValue,
      prevFilterValueRef,
      flatTreeData,
      getLabelFromNode,
      filterBy,
      dispatch,
    });
  }, [
    flatTreeData,
    filterValue,
    dispatch,
    getLabelFromNode,
    isAutocomplete,
    filterBy,
  ]);

  const items = orderedIds.map((x) => normalizedTree[x]);

  const [selectedItems, selectedItem] = useMemo(
    () => generateSelectedItems({ selectedIds, normalizedTree }),
    [normalizedTree, selectedIds],
  );

  const getTreeNodeProps = useCallback(
    (node: InternalDataNode<InputTreeNode>) => {
      const treeNodeProps = rawGetTreeNodeProps(node);

      const ref = (elem: HTMLLIElement) => {
        nodes.set(node.id, elem);
      };

      return {
        ref,
        ...treeNodeProps,
        filterValue: isAutocomplete ? filterValue : undefined,
        enableSearchHighlight: isAutocomplete
          ? enableSearchHighlight
          : undefined,
      };
    },
    [
      nodes,
      rawGetTreeNodeProps,
      isAutocomplete,
      filterValue,
      enableSearchHighlight,
    ],
  );

  const {
    onKeyDown: treeOnKeyDown,
    onBlur: treeOnBlur,
    onFocus,
    ref: focusRef,
    tabIndex: _ignoredTabIndex,
    ...treeProps
  } = getTreeProps();

  const removeSelectedItem = useCallback(
    (item: InternalDataNode<InputTreeNode>) => {
      onSelect?.({ id: item.id, isSelected: false });
    },
    [onSelect],
  );

  const handleKeyDown = useCallback<React.KeyboardEventHandler<HTMLElement>>(
    (e) =>
      _handleKeyDown({
        e,
        isOpen,
        isAutocomplete,
        highlightedId,
        selectedIds,
        openMenu,
        closeMenu,
        setFilterValue,
        isNodeMultiSelectable,
        onSelect,
        treeOnKeyDown,
      }),
    [
      isOpen,
      openMenu,
      isAutocomplete,
      highlightedId,
      closeMenu,
      isNodeMultiSelectable,
      selectedIds,
      onSelect,
      treeOnKeyDown,
    ],
  );

  const getMenuProps = useCallback<DropdownTreeGetMenuProps>(
    ({ style, width: _width, ...props }) => {
      return {
        tabIndex: -1,
        ...treeProps,
        ...props,
        style: [
          treeProps.style,
          { width: controlRef.current?.offsetWidth },
          style as StyleProp,
        ],
      };
    },
    [treeProps],
  );

  const getFooterProps: () => StylableComponent<HTMLDivElement> = useCallback(
    () => ({
      tabIndex: 0,
    }),
    [],
  );

  const getInputProps = useCallback(
    (options?: React.HTMLProps<HTMLInputElement>) => {
      return {
        ...options,
        ref: inputRef,
        value: filterValue,
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          setFilterValue(e.currentTarget.value);
          options?.onChange?.(e);
        },
        onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
          handleKeyDown(e);
          options?.onKeyDown?.(e);
        },
        onBlur: (e: React.FocusEvent<HTMLInputElement, Element>) => {
          preventOnBlurWithinComponent({
            e,
            isAutocomplete,
            inputRef,
            toggleButtonRef,
            controlRef,
            menuRef,
            closeMenu,
            treeOnBlur,
            setFilterValue,
          });
          options?.onBlur?.(e);
        },
      };
    },
    [
      filterValue,
      handleKeyDown,
      closeMenu,
      isAutocomplete,
      treeOnBlur,
      setFilterValue,
    ],
  );

  const getToggleButtonProps = useCallback(() => {
    return {
      ref: toggleButtonRef,
      tabIndex: -1,
      onClick: (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
        e.stopPropagation();
        e.preventDefault();
        toggleMenuOpen();
      },
      ...(!isAutocomplete && {
        onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => {
          e.preventDefault();
          handleKeyDown(e);
        },
      }),
      onBlur: (e: React.FocusEvent<HTMLElement>) => {
        if (isAutocomplete) {
          return;
        }
        preventOnBlurWithinComponent({
          e,
          isAutocomplete,
          inputRef,
          toggleButtonRef,
          controlRef,
          menuRef,
          closeMenu,
          treeOnBlur,
          setFilterValue,
        });
      },
      onFocus: (e: React.FocusEvent<HTMLElement>) => {
        e.stopPropagation();
        e.preventDefault();
        if (!isAutocomplete) {
          onFocus();
        }
      },
    };
  }, [
    isAutocomplete,
    toggleMenuOpen,
    handleKeyDown,
    closeMenu,
    treeOnBlur,
    onFocus,
    setFilterValue,
  ]);

  const itemToString = useCallback(
    (item: InternalDataNode<InputTreeNode> | null) =>
      item ? getLabelFromNode(item.original) : '',
    [getLabelFromNode],
  );

  useEffect(() => {
    _mergeRefs({ isAutocomplete, isOpen, focusRef, inputRef, toggleButtonRef });
  }, [isAutocomplete, isOpen, focusRef]);

  const getControlProps: () => Omit<
    Partial<ReturnType<typeof getToggleButtonProps>>,
    'ref'
  > & {
    getToggleButtonProps: (
      options?: HTMLProps<HTMLElement>,
      otherOptions?: unknown,
    ) => Omit<Partial<ReturnType<typeof getToggleButtonProps>>, 'ref'>;
  } = useCallback(() => {
    if (isAutocomplete) {
      return {
        ref: controlRef,
        getToggleButtonProps,
        onKeyDown: handleKeyDown,
        tabIndex: 0,
      };
    }

    const { ref, ...props } = getToggleButtonProps();

    return {
      ref: (controlElement: HTMLDivElement) => {
        ref.current = controlElement as unknown as HTMLButtonElement;
        controlRef.current = controlElement;
      },
      getToggleButtonProps: (x = {}) => x, // This is applied through getControlProps for non-autocomplete menus
      openMenu: noop, // Overwrite openMenu.  We don't need to call open menu, getControlProps handles it
      ...props,
      tabIndex: 0,
    };
  }, [getToggleButtonProps, handleKeyDown, isAutocomplete]);

  const sharedReturn = {
    treeProps: {
      isOpen,
      getMenuProps,
      getFooterProps,
      highlightedId,
      ...overlayProps,
    },
    normalizedTree,
    orderedIds,
    getTreeNodeProps,
    dispatch,
    setInputValue: setFilterValue,
    ...useTreeReturnValue,
    items,
  };

  const sharedControlProps = {
    style: {
      outline: 'none',
    },
    getInputProps,
    getSelectedItemProps,
    isOpen,
    openMenu,
    selectedItems: selectedItems || null,
    selectedItem: selectedItem || null,
    removeSelectedItem,
    itemToString,
    ...getControlProps(),
  };

  if (isAutocomplete) {
    return {
      ...sharedReturn,
      controlProps: {
        ...sharedControlProps,
        isAutocomplete: true as const,
      },
    };
  }

  return {
    ...sharedReturn,
    controlProps: {
      ...sharedControlProps,
      isAutocomplete: false as const,
    },
  };
};

export default useDropdownTree;
